import { Phone, MapPin, BadgeCheck, ArrowUpRight } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { COMPANY, PHONE_DISPLAY, PHONE_LINK, WA_LINK, ADDRESS_LINES, DIRECTIONS_LINK, GSTIN } from "../data/site";

const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0 text-brand-red">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const ContactSection = () => (
  <section id="contact" data-testid="contact-section" className="bg-white py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        kicker="Contact"
        title="Get a Quote for Your Project"
        desc="Share your requirement — our team responds with product details and pricing, usually the same day."
      />
      <div className="mt-14 grid lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-5">
          <div className="h-full bg-brand-coal p-7 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 noise-overlay opacity-[0.06]" />
            <div className="relative">
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand-red">Manufactured By</div>
              <h3 className="mt-2 font-display text-2xl font-semibold uppercase tracking-tight text-white leading-tight">
                {COMPANY}
              </h3>
              <p className="mt-1 text-xs tracking-[0.2em] uppercase text-white/40">MODORS Steel Door Frames</p>

              <div className="mt-9 space-y-5">
                <a href={PHONE_LINK} data-testid="contact-call-btn" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 border border-white/20 flex items-center justify-center group-hover:border-brand-red group-hover:bg-brand-red transition-colors duration-300">
                    <Phone className="w-5 h-5 text-brand-red group-hover:text-white transition-colors duration-300" />
                  </span>
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/40">Call / WhatsApp</span>
                    <span className="block text-lg font-semibold text-white group-hover:text-brand-red transition-colors duration-300">{PHONE_DISPLAY}</span>
                  </span>
                </a>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 border border-white/20 flex items-center justify-center group-hover:border-brand-red group-hover:bg-brand-red transition-colors duration-300">
                    <WhatsAppGlyph />
                  </span>
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/40">WhatsApp</span>
                    <span className="block text-sm font-medium text-white/80">Chat with our sales team</span>
                  </span>
                </a>
                <a href={DIRECTIONS_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-link" className="flex items-start gap-4 group">
                  <span className="w-11 h-11 shrink-0 border border-white/20 flex items-center justify-center group-hover:border-brand-red group-hover:bg-brand-red transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-brand-red group-hover:text-white transition-colors duration-300" />
                  </span>
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/40">Factory Address</span>
                    <span className="block text-sm font-medium text-white/80 leading-relaxed">
                      {ADDRESS_LINES.map((l) => (
                        <span key={l} className="block">{l}</span>
                      ))}
                    </span>
                    <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold tracking-wider uppercase text-brand-red">
                      Get Directions <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 border border-white/20 flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-brand-red" />
                  </span>
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/40">GSTIN</span>
                    <span className="block text-sm font-medium tracking-wider text-white/80">{GSTIN}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-7">
          <QuoteForm />
        </Reveal>
      </div>
    </div>
  </section>
);
